import { useState } from 'react'
import { Repeat } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { Modal, DrawerFooter, Field, Input, Badge, Banner, InlineError, fmtMoney, fmtMonth, useToast } from '../../kit'

const TYPE_TONE = { 'เงินกู้': 'violet', 'ค่าฝึกอบรม': 'brand' }

/**
 * หักผ่อนเพิ่ม — take MORE installments of a หักประจำ plan in this payroll month than
 * gen2 calculated (the staff member wants to pay it off faster).
 *
 * Writes through POST /api/hr/salary-recurring-extra → Xano api 681, which files one
 * manual salary_em row PER installment carrying the plan's recurring_uuid. That link
 * is the point: gen2 and the หักประจำ tab both count those rows as installments paid,
 * so the plan ends early instead of being deducted again. (The old workaround — an
 * `อื่นๆ` row with a remark — was invisible to both.)
 *
 * The browser sends only WHICH plan and HOW MANY. Amount, type and the cap come from
 * the plan in Xano; `left` here (extraInstallmentsLeft) only stops the dialog from
 * offering what Xano would refuse.
 *
 *   plans  [{ entry, collected, left }] — this employee's plans with left > 0
 */
export default function RecurringExtraModal({ employeeId, employeeLabel, plans, year, month, onClose, onSaved }) {
  const toast = useToast()
  const [uuid, setUuid] = useState(plans.length === 1 ? plans[0].entry.uuid : '')
  const [count, setCount] = useState('1')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const plan = plans.find((p) => p.entry.uuid === uuid) || null
  const k = Number(count)
  const kOk = Number.isInteger(k) && k >= 1 && plan && k <= plan.left
  const per = Number(plan?.entry.amount || 0)
  const n = Number(plan?.entry.N || 0)
  const monthLabel = fmtMonth(`${year}-${String(month).padStart(2, '0')}-01`, { long: true })

  const status = !plan ? 'เลือกรายการหักประจำ'
    : !kOk ? `จำนวนงวดต้องอยู่ระหว่าง 1–${plan.left}`
      : null

  async function save() {
    if (!kOk) return
    setSaving(true); setErr('')
    try {
      const r = await postJson('/api/hr/salary-recurring-extra', {
        employee_id: Number(employeeId),
        recurring_uuid: uuid,
        installments: k,
        year, month,
        note: note.trim(),
      })
      toast.success(`หักเพิ่ม ${k} งวด · ${plan.entry.type} ชำระแล้ว ${r?.collected_after ?? plan.collected + k}/${r?.N ?? n} งวด`)
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'หักผ่อนเพิ่มไม่สำเร็จ'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title="หักผ่อนเพิ่ม"
      subtitle={`${employeeLabel} · รอบ ${monthLabel}`}
      icon={Repeat}
      onClose={onClose}
      busy={saving}
      dirty={Boolean(note.trim()) || count !== '1'}
      footer={
        <DrawerFooter
          onCancel={onClose}
          onSave={save}
          saving={saving}
          canSave={kOk}
          status={status}
          saveLabel={kOk ? `หักเพิ่ม ${k} งวด` : 'หักเพิ่ม'}
        />
      }
    >
      <div className="space-y-4">
        <Field label="รายการหักประจำ" required>
          <div className="space-y-2">
            {plans.map(({ entry, collected, left }) => {
              const on = entry.uuid === uuid
              return (
                <button
                  key={entry.uuid}
                  type="button"
                  onClick={() => { setUuid(entry.uuid); if (Number(count) > left) setCount(String(left)) }}
                  aria-pressed={on}
                  className={`w-full text-left rounded-xl border px-3.5 py-2.5 transition-colors ${
                    on ? 'border-brand-400 bg-brand-50/50 ring-4 ring-brand-500/10' : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Badge tone={TYPE_TONE[entry.type] || 'slate'}>{entry.type}</Badge>
                    <span className="text-[13.5px] text-slate-700 truncate">{entry.remark || ''}</span>
                    <span className="ml-auto text-[12.5px] tabular-nums text-slate-500 whitespace-nowrap">
                      ชำระแล้ว {collected}/{entry.N} งวด
                    </span>
                  </div>
                  <div className="mt-1 text-[12px] text-slate-400 tabular-nums">
                    งวดละ {fmtMoney(entry.amount)} · หักเพิ่มได้อีกไม่เกิน {left} งวด
                  </div>
                </button>
              )
            })}
          </div>
        </Field>

        <Field
          label="จำนวนงวดที่หักเพิ่ม"
          required
          hint={plan ? `ยอดหักเพิ่ม ${fmtMoney(per * (kOk ? k : 0))} บาท` : undefined}
        >
          <Input
            type="number" inputMode="numeric" min={1} max={plan?.left || 1} step={1}
            value={count} onChange={(e) => setCount(e.target.value)}
            disabled={!plan} className="w-32 tabular-nums"
          />
        </Field>

        <Field label="หมายเหตุ" hint="ต่อท้ายในรายการเงินเดือน เช่น “ขอผ่อนเร็วขึ้น”">
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} placeholder="ไม่บังคับ" />
        </Field>

        {plan && kOk && (
          <Banner tone="info">
            บันทึกเป็นรายการเพิ่มเอง {k} รายการในรอบ {monthLabel} และนับเป็นงวดที่ชำระแล้ว
            — หักประจำจะเป็น {plan.collected + k}/{n} งวด และสิ้นสุดเร็วขึ้น {k} เดือน
            ลบรายการนี้ออกเมื่อใด งวดก็คืนกลับ
          </Banner>
        )}

        <InlineError>{err}</InlineError>
      </div>
    </Modal>
  )
}
